import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { useNavigate } from 'react-router-dom';

const OFFER_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const URGENCY_LAST_HOURS = 24;
const URGENCY_LAST_DAYS = 3;

function getOfferStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-ai-offer-start-at-${userId}`;
}

function formatRemainingTime(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
}

const SubscriptionAiPromptDialog = ({ open, onClose, skillName = 'Writing' }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const discountCode = 'scoreswift25';
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [offerStartTs, setOfferStartTs] = useState(null);

  useEffect(() => {
    if (!open) return;
    const storageKey = getOfferStorageKey();
    const storedValue = Number(localStorage.getItem(storageKey));
    const hasValidStoredValue = Number.isFinite(storedValue) && storedValue > 0;
    const startTs = hasValidStoredValue ? storedValue : Date.now();

    if (!hasValidStoredValue) {
      localStorage.setItem(storageKey, String(startTs));
    }

    setOfferStartTs(startTs);
    setNowTs(Date.now());

    const timerId = setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => clearInterval(timerId);
  }, [open]);

  const remainingMs = useMemo(() => {
    if (!offerStartTs) return OFFER_DURATION_MS;
    const expiresAt = offerStartTs + OFFER_DURATION_MS;
    return expiresAt - nowTs;
  }, [offerStartTs, nowTs]);

  const isOfferExpired = remainingMs <= 0;
  const isFinalHours = remainingMs > 0 && remainingMs <= URGENCY_LAST_HOURS * 60 * 60 * 1000;
  const isFinalDays = remainingMs > 0 && remainingMs <= URGENCY_LAST_DAYS * 24 * 60 * 60 * 1000;
  const urgencyLabel = isFinalHours
    ? 'Final hours'
    : isFinalDays
      ? 'Ending soon'
      : 'Limited offer';
  const urgencyTextColor = isFinalHours ? '#B42318' : isFinalDays ? '#C2410C' : '#5B4A7A';
  const urgencyBg = isFinalHours ? '#FFF1F3' : isFinalDays ? '#FFF7ED' : '#F4F0FF';

  const handleCopyCode = async () => {
    if (isOfferExpired) return;
    try {
      await navigator.clipboard.writeText(discountCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      setCopied(false);
    }
  };

  const handleViewPlans = () => {
    onClose?.();
    navigate('/plan-info');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 4 },
          background: 'linear-gradient(145deg, #fffaf3 0%, #f6f0ff 52%, #eef9ff 100%)',
        }}
      >
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: '20px',
            display: 'grid',
            placeItems: 'center',
            mb: 2,
            color: '#fff',
            background: 'linear-gradient(135deg, #996CFE 0%, #FF9F43 100%)',
            boxShadow: '0 14px 34px rgba(153, 108, 254, 0.28)',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 34 }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 900, color: '#20153d', mb: 1, fontSize: { xs: 26, sm: 32 } }}>
          Unlock AI scoring for {skillName}
        </Typography>

        <Typography sx={{ color: '#51476b', fontSize: 16, lineHeight: 1.7, mb: 2.5 }}>
          Submit your response for detailed AI feedback, band-score insights, and practical tips to improve faster.
        </Typography>

        {!isOfferExpired && (
          <Box
            sx={{
              mb: 2.5,
              p: 1.4,
              borderRadius: '12px',
              border: `1px solid ${isFinalHours ? '#FECDD3' : isFinalDays ? '#FDBA74' : '#D6BCFA'}`,
              backgroundColor: urgencyBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ color: urgencyTextColor, fontWeight: 900, fontSize: 13.5 }}>
              {urgencyLabel}: {formatRemainingTime(remainingMs)} left
            </Typography>
            <Typography sx={{ color: urgencyTextColor, fontWeight: 700, fontSize: 12.5 }}>
              Offer auto-expires in your account
            </Typography>
          </Box>
        )}

        <Stack spacing={1.2} sx={{ mb: 2.5 }}>
          {[
            'Get estimated IELTS band scoring with detailed feedback.',
            'See strengths, weak areas, and what to improve next.',
            'Practice with clearer guidance before your real exam.',
          ].map((text) => (
            <Box key={text} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CheckCircleOutlineIcon sx={{ color: '#37965E', fontSize: 20 }} />
              <Typography sx={{ color: '#33294f', fontWeight: 700, fontSize: 14.5 }}>{text}</Typography>
            </Box>
          ))}
        </Stack>

        {!isOfferExpired && (
          <Box
            sx={{
              p: 2,
              borderRadius: '18px',
              border: `1px dashed ${isFinalHours ? '#FDA4AF' : 'rgba(255, 159, 67, 0.8)'}`,
              background: isFinalHours ? 'rgba(255, 241, 242, 0.95)' : 'rgba(255, 255, 255, 0.72)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferOutlinedIcon sx={{ color: isFinalHours ? '#B42318' : '#FF8A00' }} />
              <Typography sx={{ color: '#4b3d72', fontWeight: 800 }}>
                Extra 25% discount
              </Typography>
            </Box>
            <Chip
              label={copied ? 'Code copied' : discountCode}
              onClick={handleCopyCode}
              role="button"
              title="Click to copy discount code"
              sx={{
                fontWeight: 900,
                letterSpacing: 0.7,
                color: '#7a3d00',
                background: '#FFE0B2',
                border: '1px solid #FFB74D',
                cursor: 'pointer',
                '&:hover': {
                  background: '#FFD28A',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, pb: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} sx={{ color: '#655b78', fontWeight: 800, textTransform: 'none' }}>
          Maybe later
        </Button>
        <Button
          onClick={handleViewPlans}
          variant="contained"
          sx={{
            borderRadius: '999px',
            px: 3,
            py: 1,
            fontWeight: 900,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
            boxShadow: '0 10px 22px rgba(153, 108, 254, 0.28)',
          }}
        >
          View subscription plans
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionAiPromptDialog;
